class PremiumFeatureBenefits extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.motionEnabled = this.dataset.motionEnabled === 'true' && !this.reduceMotion;
    this.videos = Array.from(this.querySelectorAll('video'));
    this.onClick = this.handleClick.bind(this);
    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onVisibilityChange = this.handleVisibilityChange.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.videos.forEach((video) => {
      video.addEventListener('play', this.handleVideoState);
      video.addEventListener('pause', this.handleVideoState);
      video.addEventListener('ended', this.handleVideoState);
    });

    if (!this.motionEnabled) this.dataset.revealed = 'true';
    requestAnimationFrame(() => this.classList.add('is-ready'));
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: '120px 0px',
        threshold: 0.12,
      });
      this.observer.observe(this);
    } else {
      this.dataset.revealed = 'true';
      this.syncAutoplay(true);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.videos?.forEach((video) => {
      video.removeEventListener('play', this.handleVideoState);
      video.removeEventListener('pause', this.handleVideoState);
      video.removeEventListener('ended', this.handleVideoState);
    });
  }

  handleIntersection = (entries) => {
    const visible = Boolean(entries[0]?.isIntersecting);
    this.inView = visible;
    if (visible) {
      this.dataset.revealed = 'true';
      this.syncAutoplay(true);
    } else {
      this.pauseVideos();
    }
  };

  handleVisibilityChange() {
    if (document.hidden) this.pauseVideos();
    else if (this.inView) this.syncAutoplay(true);
  }

  handleClick(event) {
    const toggle = event.target.closest?.('[data-media-toggle]');
    if (!toggle || !this.contains(toggle)) return;
    const media = toggle.closest('[data-feature-media]');
    const video = this.getVisibleVideo(media);
    if (!video) return;

    if (video.paused) {
      this.pauseVideos(video);
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const target = event.target.closest?.('[data-benefit-card], [data-media-toggle], .pfb__button');
    if (!target || !this.contains(target) || this.reduceMotion) return;
    if (target.matches('[data-benefit-card]') && this.dataset.cardClick !== 'true') return;

    if (target.matches('[data-benefit-card]')) {
      const bounds = target.getBoundingClientRect();
      const x = Math.max(0, Math.min(event.clientX - bounds.left, bounds.width));
      const y = Math.max(0, Math.min(event.clientY - bounds.top, bounds.height));
      target.style.setProperty('--pfb-click-x', `${x}px`);
      target.style.setProperty('--pfb-click-y', `${y}px`);
    }
    target.classList.remove('is-clicked');
    void target.offsetWidth;
    requestAnimationFrame(() => {
      target.classList.add('is-clicked');
      window.setTimeout(() => target.classList.remove('is-clicked'), 760);
    });
  }

  handleBlockSelect(event) {
    const card = event.target.closest?.('[data-benefit-card]');
    if (!card || !this.contains(card)) return;
    this.dataset.revealed = 'true';
    card.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
    card.classList.remove('is-editor-selected');
    requestAnimationFrame(() => card.classList.add('is-editor-selected'));
    window.setTimeout(() => card.classList.remove('is-editor-selected'), 900);
  }

  handleVideoState = (event) => {
    const media = event.currentTarget.closest('[data-feature-media]');
    if (!media) return;
    media.dataset.playing = String(!event.currentTarget.paused && !event.currentTarget.ended);
    const toggle = media.querySelector('[data-media-toggle]');
    if (toggle) toggle.setAttribute('aria-label', event.currentTarget.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
  };

  getVisibleVideo(scope = this) {
    return Array.from(scope?.querySelectorAll('video') || []).find((video) => video.offsetParent !== null) || null;
  }

  syncAutoplay(shouldPlay) {
    if (!shouldPlay || this.reduceMotion || this.dataset.videoAutoplay !== 'true' || document.hidden) return;
    const video = this.getVisibleVideo();
    if (!video) return;
    this.pauseVideos(video);
    video.muted = true;
    video.play().catch(() => {});
  }

  pauseVideos(except) {
    this.videos?.forEach((video) => {
      if (video !== except && !video.paused) video.pause();
    });
  }
}

if (!customElements.get('premium-feature-benefits')) {
  customElements.define('premium-feature-benefits', PremiumFeatureBenefits);
}
